import type { CSSProperties } from "react"
import {
  UV_INSERT_TEXT_LIST_BULLET_DEFAULTS,
  type UVInsertFaceElementTextListConfig,
} from "@shared/models/uv-inserts"
import { getBirthstoneStarPoints } from "@shared/view/uv-inserts/birthstone-star"
import {
  getUvInsertTextListRows,
  type UvInsertTextListRow,
} from "@shared/view/uv-inserts/text-list-rows"
import { getUvInsertTextListRuleMetrics } from "@shared/view/uv-inserts/text-list-rule"
import {
  getUvInsertTextListMomentMargin,
  getUvInsertTextListStarMetrics,
  type UvInsertTextListStarMetrics,
} from "@shared/view/uv-inserts/text-list-star"

/** Screen-reader name for the whole list — the element has no visible heading on the card. */
const TEXT_LIST_ARIA_LABEL = "Your months and moments"

/** One rendered row. Built by the shared builder so the canvas preview cannot drift from the DOM. */
export type VisualiserTextListRow = UvInsertTextListRow

/** The styles for the decorative vertical rule, or `null` when the list draws none. */
export type VisualiserTextListRuleStyles = {
  /** The absolutely-positioned strip the line and dots live in, on the list's anchor side. */
  containerStyle: CSSProperties
  /** The line itself, centred in the strip and spanning it top to bottom. */
  lineStyle: CSSProperties
  /** One end dot; both ends share it. `null` when the rule asks for no dots. */
  dotStyle: CSSProperties | null
}

/** The AL birthstone star drawn before a row's headline when `bullet.shape` is `'star'`. */
export type VisualiserTextListStar = {
  /** Width and height of the star's square box, in px. */
  size: number
  /** Px between the star's right point and the headline's first letter. */
  gap: number
  /** SVG `<polygon points>` for the star outline, laid out in a `size` × `size` box. */
  points: string
  /** Radius of the centre circle, in px. */
  circleRadius: number
  fillColor: string
  outlineColor: string
  outlineWidth: number
  /**
   * Height of the headline line in px: the taller of the text's own line box and the star. The
   * canvas cart preview applies the same rule, so both renderers stack rows identically.
   */
  lineHeightPx: number
}

export type VisualiserTextListViewModelOptions = {
  element: UVInsertFaceElementTextListConfig
  /** The host-populated text; only this element's month/year/moment keys are read. */
  textByKey: Record<string, string>
}

/**
 * The star's view model for a TEXT_LIST whose bullet shape is `'star'`: the shared metrics plus the
 * SVG `points` string for the outline, laid out in a `size` × `size` box.
 */
function buildStar(metrics: UvInsertTextListStarMetrics): VisualiserTextListStar {
  const { size } = metrics

  return {
    ...metrics,
    points: getBirthstoneStarPoints({ cx: size / 2, cy: size / 2, size })
      .map(([x, y]) => `${x},${y}`)
      .join(" "),
  }
}

/**
 * Presentation state for the read-only TEXT_LIST element.
 *
 * Rows come from `getUvInsertTextListRows`, which the canvas cart preview reads too: rows without a
 * month are dropped rather than left blank, so a skipped stone leaves no gap on the printed card,
 * and the headline joins the month to a full four-digit year when the customer typed one.
 *
 * The optional vertical rule is sized the same way, from `getUvInsertTextListRuleMetrics`. It lives
 * INSIDE the element box — the list is `overflow: hidden` (load-bearing: an unbreakable moment would
 * otherwise run across the polaroid), so a rule hung outside would be clipped away. The box reserves
 * `reservedWidth` of padding on the anchor side and `overhang` top and bottom; an absolutely
 * positioned child resolves against the padding box, so `top/bottom/left|right: 0` lands it exactly
 * on those edges with nothing to clip. That is also what makes it flex: the list's height is its
 * rows, so the rule is as long as the rows drawn.
 */
export function getVisualiserTextListViewModel(options: VisualiserTextListViewModelOptions) {
  const { element, textByKey } = options

  const rows = getUvInsertTextListRows({ element, textByKey })
  // No rows, no rule: a card with no stones chosen shows nothing, not a bare line down an empty
  // column. The canvas renderer returns early on the same condition.
  const metrics = rows.length > 0 ? getUvInsertTextListRuleMetrics({ element }) : null
  const rightAligned = element.align === "right"

  const bulletShape = element.bullet.shape ?? UV_INSERT_TEXT_LIST_BULLET_DEFAULTS.shape
  const starMetrics = getUvInsertTextListStarMetrics({ element })
  const star = starMetrics ? buildStar(starMetrics) : null

  const listStyle: CSSProperties = {
    position: "absolute",
    top: element.positioning.top,
    bottom: element.positioning.bottom,
    left: element.positioning.left,
    right: element.positioning.right,
    width: element.size.width,
    minWidth: element.size.minWidth,
    maxWidth: element.size.maxWidth,
    minHeight: element.size.minHeight,
    maxHeight: element.size.maxHeight,
    fontFamily: element.font,
    fontSize: typeof element.fontSize === "number" ? `${element.fontSize}px` : element.fontSize,
    fontWeight: element.fontWeight,
    lineHeight: element.lineHeight,
    color: element.color || "var(--color-black)",
    // A fixed row pitch replaces the flex gap: each row owns its whole pitch via `rowStyle`.
    gap: element.rowPitch !== undefined ? "0px" : `${element.rowGap}px`,
    // Only a list that opts into a rule is padded — everything else keeps the exact style object it
    // had before the rule existed.
    ...(metrics
      ? {
          paddingTop: `${metrics.overhang}px`,
          paddingBottom: `${metrics.overhang}px`,
          ...(rightAligned
            ? { paddingRight: `${metrics.reservedWidth}px` }
            : { paddingLeft: `${metrics.reservedWidth}px` }),
        }
      : {}),
    ...(element.style as CSSProperties),
  }

  // `currentColor` rather than a hard-coded black: an element that declares no colour of its own
  // inherits the face's, exactly as the bullets do.
  const ruleColor = metrics?.color ?? "currentColor"

  const rule: VisualiserTextListRuleStyles | null = metrics
    ? {
        containerStyle: {
          width: `${metrics.stripWidth}px`,
          ...(rightAligned ? { right: 0 } : { left: 0 }),
        },
        lineStyle: { width: `${metrics.lineWidth}px`, backgroundColor: ruleColor },
        dotStyle:
          metrics.dotRadius > 0
            ? {
                width: `${metrics.dotRadius * 2}px`,
                height: `${metrics.dotRadius * 2}px`,
                backgroundColor: ruleColor,
              }
            : null,
      }
    : null

  const rowStyle: CSSProperties =
    element.rowPitch !== undefined ? { height: `${element.rowPitch}px`, flex: "0 0 auto" } : {}

  // Only a list with a `momentOffset` moves its moment; every other list keeps the exact style it had.
  const momentMargin = getUvInsertTextListMomentMargin({ element })
  const momentStyle: CSSProperties | undefined =
    element.momentOffset !== undefined ? { marginTop: `${momentMargin}px` } : undefined

  return {
    rows,
    listStyle,
    rowStyle,
    /** Style for the moment line, or `undefined` when it stacks directly under the headline. */
    momentStyle,
    bulletShape,
    /** The star's geometry, or `null` in glyph mode. */
    star,
    /** `'right'` anchors the rows at the element's right edge; the bullet stays on the left. */
    align: element.align,
    bulletGlyph: element.bullet.glyph,
    /** An empty glyph means "no bullet" — the row then starts with its text. */
    showBullet: element.bullet.glyph !== "",
    /** Decorative rule styles, or `null` when this list draws no rule. */
    rule,
    ariaLabel: TEXT_LIST_ARIA_LABEL,
  }
}
